import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { INDUSTRIES, trackEvent } from "@/mock";
import { analyticsApi, crmApi } from "@/services/phase1Api";
import { PopupModal } from "react-calendly";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Calendar, Check, Clock, Sparkles, MessageSquare, ExternalLink } from "lucide-react";
import { DEFAULT_SITE_COPY, useSiteCopy, PageShell } from "./publicShared";

export const ContactContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const intent = params.get("intent");

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  // Pre-fill
  const prefill = location.state || {};
  const [vertical, setVertical] = useState(prefill.vertical || "general");
  const [company, setCompany] = useState(prefill.company || "");
  const [name, setName] = useState(prefill.name || "");
  const [email, setEmail] = useState(prefill.email || "");
  const [notes, setNotes] = useState(prefill.notes || "");
  const [loading, setLoading] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const [callBooked, setCallBooked] = useState(false);
  const { copy } = useSiteCopy();

  const rawCalendlyUrl = copy.calendly_url || DEFAULT_SITE_COPY.calendly_url;
  const calendlyUrl = rawCalendlyUrl && !rawCalendlyUrl.startsWith("http")
    ? `https://${rawCalendlyUrl}`
    : rawCalendlyUrl;
  const canSubmit = company && name && email;

  const labelClass = "block text-[13px] font-semibold text-[#111827] mb-2";
  const inputClass =
    "h-11 rounded-lg border-[#e5e7eb] bg-white text-[#111827] text-[14px] placeholder:text-[#9ca3af] focus-visible:ring-2 focus-visible:ring-[#1a56db]/30 focus-visible:border-[#1a56db]";

  if (callBooked) {
    return (
      <PageShell>
        <div className="bg-white py-20 lg:py-28">
          <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-10 text-center" data-testid="call-booked-thanks">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#ecfdf5] px-4 py-2 text-[13px] font-semibold text-[#059669] mb-6">
              <Check className="h-4 w-4" strokeWidth={3} />
              Meeting Scheduled
            </div>
            <h2 className="text-[32px] lg:text-[38px] font-bold text-[#111827] tracking-tight leading-[1.15]" style={{ letterSpacing: "-0.5px" }}>
              Thank You{name ? `, ${name}` : ""}!
            </h2>
            <p className="mt-5 text-[15px] text-[#4b5563] leading-[1.7]">
              Thank you for setting up a FREE initial conversation. A Zoom meeting link will be emailed to you shortly.
            </p>
            <p className="mt-3 text-[15px] text-[#4b5563] leading-[1.7]">
              This call will take less than 30 minutes and will only be high level — but it will give us both a good idea of where to start.
            </p>
            <p className="mt-3 text-[15px] font-semibold text-[#111827]">
              We look forward to meeting you!
            </p>
            <Button
              className="mt-8 h-12 rounded-lg bg-[#1a56db] px-8 text-white text-[14px] font-semibold hover:bg-[#143faa] shadow-none"
              onClick={() => navigate("/")}
              data-testid="back-to-home-btn"
            >
              <ArrowRight className="h-4 w-4 mr-2" /> Back to Home
            </Button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* Hero */}
      <section className="bg-white py-16 lg:py-20" data-testid="contact-hero">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#e8eefb] px-4 py-1.5 text-[13px] font-semibold text-[#1a56db] mb-6" data-testid="contact-hero-pill">
            <span className="h-1.5 w-1.5 rounded-full bg-[#1a56db]" />
            Get in Touch
          </div>
          <h1 className="text-[42px] sm:text-[52px] lg:text-[60px] font-bold text-[#111827] leading-[1.05] tracking-tight" style={{ letterSpacing: "-1.5px" }}>
            Let's talk about<br />your workflows
          </h1>
          <p className="mt-6 text-[16px] sm:text-[17px] text-[#4b5563] leading-[1.7] max-w-[620px] mx-auto">
            Book a free assessment call or send us a message. No pressure,
            no commitment — just a conversation about what AI can do for
            your operations.
          </p>
        </div>
      </section>

      {/* Cards */}
      <section className="bg-white pb-20 lg:pb-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Booking card */}
            <div className="rounded-2xl border border-[#e5e7eb] bg-white p-8 flex flex-col" data-testid="contact-booking-card">
              <h2 className="text-[22px] font-bold text-[#111827] mb-2" style={{ letterSpacing: "-0.02em" }}>
                Book a Free Assessment Call
              </h2>
              <p className="text-[14px] text-[#4b5563] leading-[1.6] mb-6">
                Select a time that works for you and we'll confirm within minutes.
              </p>

              {/* 3-column mini cards */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                  { icon: Clock, label: "30 min", sub: "Qualification call" },
                  { icon: Check, label: "Free", sub: "No commitment" },
                  { icon: Calendar, label: "Flexible", sub: "Pick your time" },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="rounded-lg bg-[#f9fafb] border border-[#e5e7eb] p-4 text-center">
                      <Icon className="h-5 w-5 text-[#1a56db] mx-auto mb-2" />
                      <div className="text-[14px] font-bold text-[#111827]">{item.label}</div>
                      <div className="text-[11px] text-[#9ca3af] mt-1">{item.sub}</div>
                    </div>
                  );
                })}
              </div>

              {calendlyUrl ? (
                <>
                  <Button
                    data-testid="calendly-popup-button"
                    onClick={() => setShowCalendly(true)}
                    className="h-12 w-full rounded-lg bg-[#1a56db] text-white text-[14px] font-semibold hover:bg-[#143faa] shadow-none"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    Choose a Time
                  </Button>

                  <PopupModal
                    url={calendlyUrl}
                    prefill={{
                      name,
                      email,
                      customAnswers: notes ? { a1: notes } : undefined,
                    }}
                    pageSettings={{
                      backgroundColor: "ffffff",
                      primaryColor: "1a56db",
                      textColor: "111827",
                    }}
                    rootElement={document.getElementById("root")}
                    open={showCalendly}
                    onModalClose={() => {
                      setShowCalendly(false);
                      setCallBooked(true);
                    }}
                  />

                  <div className="mt-3 text-[12px] text-[#059669] flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    Confirmation sent instantly after booking
                  </div>

                  {/* "or" divider */}
                  <div className="my-6 flex items-center gap-3">
                    <div className="flex-1 h-px bg-[#e5e7eb]" />
                    <span className="text-[12px] text-[#9ca3af]">or</span>
                    <div className="flex-1 h-px bg-[#e5e7eb]" />
                  </div>

                  {/* Prefer a quick chat */}
                  <div className="rounded-lg bg-[#f9fafb] border border-[#e5e7eb] p-4 flex items-start gap-3">
                    <div className="h-9 w-9 rounded-full bg-[#e8eefb] flex items-center justify-center shrink-0">
                      <MessageSquare className="h-4 w-4 text-[#1a56db]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-[#111827]">Prefer a quick chat?</div>
                      <div className="text-[12px] text-[#4b5563] mt-0.5">
                        Reach us directly at{" "}
                        <a href="mailto:hello@589labs.ai" className="font-semibold text-[#1a56db] hover:underline">
                          hello@589labs.ai
                        </a>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="rounded-lg bg-[#f9fafb] border border-[#e5e7eb] p-5 text-center">
                  <div className="text-[14px] font-semibold text-[#111827]">Booking temporarily unavailable</div>
                  <div className="mt-1.5 text-[12px] text-[#9ca3af]">
                    Please send a message using the form on the right — we'll reply within 24 hours.
                  </div>
                </div>
              )}
            </div>

            {/* Send message card */}
            <div className="rounded-2xl border border-[#e5e7eb] bg-white p-8" data-testid="contact-message-card">
              <h2 className="text-[22px] font-bold text-[#111827] mb-2" style={{ letterSpacing: "-0.02em" }}>
                Send a Message
              </h2>
              <p className="text-[14px] text-[#4b5563] leading-[1.6] mb-6">
                Tell us about your workflow challenge and we'll respond within one business day.
              </p>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Industry</label>
                  <Select value={vertical} onValueChange={setVertical}>
                    <SelectTrigger className={inputClass}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((i) => (
                        <SelectItem key={i.key} value={i.key}>{i.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className={labelClass}>Company</label>
                  <Input
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className={inputClass}
                    placeholder="Your company name"
                    data-testid="contact-company"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Full Name</label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={inputClass}
                      placeholder="Your name"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Email</label>
                    <Input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputClass}
                      placeholder="you@company.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Message</label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-[140px] rounded-lg border-[#e5e7eb] bg-white text-[#111827] text-[14px] placeholder:text-[#9ca3af] focus-visible:ring-2 focus-visible:ring-[#1a56db]/30 focus-visible:border-[#1a56db] resize-y"
                    placeholder="What workflow do you want to multiply first?"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  disabled={!canSubmit || loading}
                  onClick={async () => {
                    setLoading(true);
                    try {
                      await crmApi.createLead(
                        { vertical, company, full_name: name, email, phone: "", notes },
                        null
                      );
                      trackEvent("form_submit", { form: "contact" });
                      analyticsApi.track("form_submit", { form: "contact" });
                      toast.success("Message sent", {
                        description: "We've received your message. Our team will be in touch shortly.",
                      });
                      setCompany("");
                      setName("");
                      setEmail("");
                      setNotes("");
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="h-12 w-full rounded-lg bg-[#1a56db] text-white text-[14px] font-semibold hover:bg-[#143faa] shadow-none disabled:opacity-50 mt-2"
                  data-testid="contact-send-btn"
                >
                  {loading ? "Sending..." : "Send Message"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA strip */}
      <section className="bg-[#f9fafb] border-t border-[#e5e7eb] py-6" data-testid="contact-bottom-cta">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-4 w-4 text-[#1a56db] mt-1 shrink-0" />
              <div>
                <div className="text-[13px] font-semibold text-[#1a56db]">Start with a quick assessment</div>
                <p className="text-[14px] text-[#111827] mt-1 font-semibold">
                  Get your top 3 workflow opportunities, readiness snapshot, and recommended next step.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Button
                onClick={() => {
                  navigate("/");
                  setTimeout(() => {
                    document.getElementById("opportunity-score")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 150);
                }}
                className="h-11 rounded-lg bg-[#1a56db] px-5 text-white text-[13px] font-semibold hover:bg-[#143faa] shadow-none"
                data-testid="contact-bottom-cta-primary"
              >
                Get My AI Opportunity Score
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCalendly(true)}
                disabled={!calendlyUrl}
                className="h-11 rounded-lg border border-[#e5e7eb] bg-white px-5 text-[#111827] text-[13px] font-semibold hover:bg-[#f9fafb] shadow-none disabled:opacity-50"
                data-testid="contact-bottom-cta-secondary"
              >
                Book FREE Assessment Call
                <ExternalLink className="ml-2 h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

// Wrap in component that the router can import
const ContactPage = ContactContent;
export default ContactPage;
